import { useState } from 'react';
import { HelpCircle, List } from 'lucide-react';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '../ui/hover-card';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import { rxNavSearchUrl } from './CodeReferencesPanel';
import type {
  DailyMedPanelData,
  DailyMedPanelItem,
  DailyMedSevereEffect,
  DailyMedSevereEffectsForDrug,
} from './types';

/**
 * #35 (USER ORDER 2026-08-18): the DailyMed FDA-label panel that replaces the
 * standard CMA sections inside the Review & Recommendations collapsible while
 * MARCH_ADK_CMA_DAILYMED_PANEL_ENABLED is on. TEMPORARY replacement — the
 * standard renderer stays untouched and returns the moment the panel data is
 * absent.
 *
 * Layout, per the user's spec:
 * - One header per DDI severity section (the backend always sends all five,
 *   empty ones included) plus "Reported side-effects:" at the same level.
 * - An empty section renders a generic no-evidence line, never disappears.
 * - One bullet per interaction item; a question-mark icon at the end opens a
 *   hoverable, clickable tooltip with one box per drug pair: pair on top,
 *   the label's reasoning under it.
 * - `[RXNORM:xxxx]` never renders as text — it becomes the RxNav external
 *   link on the drug name (same producer as the code-reference chips).
 * - Severe-effect lines carry a small severity box colored by tier
 *   (Boxed warning > Contraindication > Warning > Adverse event, red down to
 *   yellow), each with a static non-clickable hover message. Severity is
 *   never color-only: the box always carries the tier's text label.
 */

const NO_EVIDENCE_TEXT = 'No evidence found in the indexed FDA-label corpus.';

/** Split a wrapper display name into visible text + RxNorm code (if any). */
export function splitRxNorm(display: string): { name: string; rxcui: string } {
  const match = /\s*\[RXNORM:(\d+)\]\s*/.exec(display || '');
  if (!match) return { name: (display || '').trim(), rxcui: '' };
  return {
    name: (display || '').replace(match[0], ' ').replace(/\s+/g, ' ').trim(),
    rxcui: match[1],
  };
}

/** Drug name; linked out to RxNav when the display carried an RxNorm code. */
function DrugName({ display }: { display: string }) {
  const { name, rxcui } = splitRxNorm(display);
  if (!rxcui) return <span className="font-medium">{name}</span>;
  return (
    <a
      href={rxNavSearchUrl(rxcui)}
      target="_blank"
      rel="noopener noreferrer"
      className="font-medium text-primary underline decoration-primary/40 underline-offset-2 hover:decoration-primary"
    >
      {name}
    </a>
  );
}

// Tier ramp, red down to yellow (user spec). Text label always present, so
// the color is reinforcement, never the sole carrier (a11y/safety rule 3).
const TIER_META: Record<string, { label: string; message: string; box: string }> = {
  BOXED_WARNING: {
    label: 'Boxed warning',
    message: "The FDA's most serious warning; can be fatal.",
    box: 'bg-red-500/10 border-red-500/40 text-red-600 dark:text-red-400',
  },
  CONTRAINDICATION: {
    label: 'Contraindication',
    message: 'Do not give the drug at all.',
    box: 'bg-orange-600/10 border-orange-600/40 text-orange-700 dark:text-orange-400',
  },
  WARNING: {
    label: 'Warning',
    message: 'Serious, manageable.',
    box: 'bg-amber-500/10 border-amber-500/40 text-amber-700 dark:text-amber-400',
  },
  ADVERSE_EVENT: {
    label: 'Adverse event',
    message: 'Side effects, with a frequency.',
    box: 'bg-yellow-400/10 border-yellow-500/40 text-yellow-700 dark:text-yellow-300',
  },
};

/** The small severity box (reference-source-box anatomy) with its static,
 * non-clickable hover message. */
function TierBox({ tier }: { tier: string }) {
  const meta = TIER_META[tier] ?? {
    label: tier,
    message: '',
    box: 'bg-muted border-border text-muted-foreground',
  };
  const box = (
    <span
      data-testid={`tier-box-${tier}`}
      className={`inline-flex shrink-0 items-center rounded border px-1.5 py-0.5 text-[10.5px] font-medium leading-none ${meta.box}`}
    >
      {meta.label}
    </span>
  );
  if (!meta.message) return box;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{box}</TooltipTrigger>
      <TooltipContent variant="clinical" side="top">
        {meta.message}
      </TooltipContent>
    </Tooltip>
  );
}

function SectionHeader({ text }: { text: string }) {
  return (
    <div className="text-[11px] font-medium uppercase tracking-wide text-brand-text">
      {text}
    </div>
  );
}

function EmptyLine() {
  return <div className="text-[12.5px] text-muted-foreground">{NO_EVIDENCE_TEXT}</div>;
}

/** The hoverable, clickable explanation behind one interaction item: one box
 * per pair, the pair on top, the reasoning under it. When the merged item's
 * pair and reasoning counts differ (the #32 collapse can merge either side
 * independently), all pairs head a single box over the pooled reasonings —
 * honest attribution beats guessed pairings. */
function ItemExplainer({ item }: { item: DailyMedPanelItem }) {
  const pairs = item.pairs ?? [];
  const reasonings = item.reasonings ?? [];
  if (!pairs.length && !reasonings.length) return null;
  const matched = pairs.length > 0 && pairs.length === reasonings.length;
  const boxes = matched
    ? pairs.map((pair, i) => ({ pairs: [pair], reasonings: [reasonings[i]] }))
    : [{ pairs, reasonings }];
  return (
    <HoverCard openDelay={150} closeDelay={120}>
      <HoverCardTrigger asChild>
        <button
          type="button"
          aria-label="Why this instruction"
          className="ml-1 inline-flex shrink-0 items-center align-middle text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
        >
          <HelpCircle className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      </HoverCardTrigger>
      <HoverCardContent side="top" align="start" className="w-80 max-h-72 overflow-y-auto p-2 space-y-2">
        {boxes.map((box, i) => (
          <div key={i} className="rounded-md border border-border bg-card p-2.5 space-y-1.5">
            {box.pairs.length > 0 && (
              <div className="text-[12.5px] leading-snug space-y-0.5">
                {box.pairs.map((pair, j) => (
                  <div key={j}>
                    <DrugName display={pair.drug_a} />
                    <span className="text-muted-foreground"> + </span>
                    <DrugName display={pair.drug_b} />
                  </div>
                ))}
              </div>
            )}
            {box.reasonings.filter(Boolean).map((reason, j) => (
              <p key={j} className="m-0 text-[12px] leading-[1.55] text-muted-foreground">
                {reason}
              </p>
            ))}
          </div>
        ))}
      </HoverCardContent>
    </HoverCard>
  );
}

/** Tokens that identify ONE drug, taken from the payload's own displays.
 *
 * A display reads like "24 HR metoprolol succinate 100 MG Extended Release
 * Oral Tablet". Most of those words - Oral, Tablet, Extended, Release - name a
 * dose form, not a drug, and they recur across products. A token therefore
 * counts as identifying only when it appears in exactly ONE of the displays on
 * this response. That test is computed from the response itself, so nothing
 * here is a hardcoded word list (rule 12): on a roster of two oral tablets the
 * word "tablet" is shared and drops out automatically, and no maintenance is
 * needed when a new dose form appears. */
function identifyingTokens(displays: string[]): Map<string, Set<string>> {
  const perDisplay = displays.map((d) =>
    new Set(
      splitRxNorm(d)
        .name.toLowerCase()
        .split(/[^a-z]+/)
        .filter((w) => w.length >= 4),
    ),
  );
  const seenIn = new Map<string, number>();
  perDisplay.forEach((set) => set.forEach((w) => seenIn.set(w, (seenIn.get(w) ?? 0) + 1)));
  const out = new Map<string, Set<string>>();
  displays.forEach((d, i) => {
    out.set(d, new Set([...perDisplay[i]].filter((w) => (seenIn.get(w) ?? 0) === 1)));
  });
  return out;
}

/** Does this action sentence already name the drug itself? */
function actionNames(action: string, display: string, index: Map<string, Set<string>>) {
  const words = new Set(action.toLowerCase().split(/[^a-z]+/));
  const tokens = index.get(display);
  if (!tokens || tokens.size === 0) return false;
  return [...tokens].some((w) => words.has(w));
}

/** #53: the drugs an interaction item is about, ahead of the instruction.
 *
 * Measured live 2026-08-18: 7 of 11 rendered items named no drug at all
 * ("Avoid concomitant use.", "Monitor for increased seizure risk."), so the
 * only clinical content sat behind the hover - unreachable on a touch screen
 * or a printout, and unscannable when triaging six items.
 *
 * REOPENED the same day by USER ORDER ("why in many items the 'pair' appears
 * in the text?? Fix it."): the model's own sentence often names the drugs too,
 * so the clinician read each drug twice on one line.
 *
 * The rule settled here is that ONE producer states identity per line, and the
 * line's own text wins when it has it. So this prefix renders only when the
 * action does NOT already name both drugs - which also, for the first time,
 * covers the merged multi-pair item: previously it rendered no prefix at all
 * and relied on the model happening to name the drugs.
 *
 * Why not the other direction - strip the names from the action, or tell the
 * model to omit them? Both were rejected against measured harm. The same
 * narrative feeds the COMMAND LINE, which has no chips, so an action reading
 * "Consider dosage reduction." would be unusable there; and the model's
 * placement of names was already measured as nondeterministic, so a prompt
 * clause could not be relied on. This test is deterministic, costs nothing,
 * and cannot mangle a sentence's grammar. */
function ItemSubjects({ item }: { item: DailyMedPanelItem }) {
  const pairs = item.pairs ?? [];
  if (pairs.length === 0) return null;
  const displays = [...new Set(pairs.flatMap((p) => [p.drug_a, p.drug_b]))].filter(Boolean);
  const index = identifyingTokens(displays);
  const action = item.action ?? '';
  const missing = displays.filter((d) => !actionNames(action, d, index));
  // The text carries every drug already: it is the single source of identity.
  if (missing.length === 0) return null;
  return (
    <span className="text-muted-foreground">
      {pairs.map((pair, i) => (
        <span key={i}>
          {i > 0 ? '; ' : null}
          <DrugName display={pair.drug_a} />
          {' + '}
          <DrugName display={pair.drug_b} />
        </span>
      ))}
      {': '}
    </span>
  );
}

/** One tier's rows for one drug (#65).
 *
 * Every block states its COUNT beside the tier label. That count is
 * non-negotiable: without it a digest silently becomes the whole truth, and a
 * clinician reading one summarised sentence has no way to tell whether it
 * stands for two contraindications or twenty-three. */
function TierBlock({
  tier,
  rows,
  digest,
}: {
  tier: string;
  rows: DailyMedSevereEffect[];
  /** When set, this replaces the rows until the reader opens the full list. */
  digest?: string;
}) {
  if (rows.length === 0) return null;
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-1.5">
        <TierBox tier={tier} />
        <span className="text-[11.5px] text-muted-foreground">({rows.length})</span>
      </div>
      {digest ? (
        <p className="m-0 ml-1 text-[12.5px] leading-[1.55]">{digest}</p>
      ) : (
        <ul className="ml-1 space-y-0.5">
          {rows.map((effect, j) => (
            <li key={j} className="flex items-start gap-1.5 text-[12.5px] leading-[1.55]">
              <span className="mt-[7px] h-1 w-1 rounded-full bg-muted-foreground/60 shrink-0" />
              <span className="min-w-0 flex-1">
                {effect.risk}
                {effect.frequency ? (
                  <span className="text-muted-foreground"> [{effect.frequency}]</span>
                ) : null}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** #65 (USER-APPROVED DESIGN 2026-08-18): boxed warnings listed in full,
 * contraindications summarised, the full list one click away.
 *
 * Why the two tiers are treated differently. Being boxed IS the FDA's own
 * severity marker, so choosing "the most serious boxed warning" is close to a
 * no-op inside that tier - and a boxed warning dropped by a summariser would
 * be invisibly missing rather than visibly truncated. Contraindications are
 * the real density problem: measured on one live roster they run 23, 19, 19
 * and 14 on four of six drugs, against 11 and 8 boxed on the two opioids.
 *
 * #41 (USER DECISION, "go entirely"): the Warning and Adverse-event tiers do
 * not reach this component at all. The filter lives in the backend carrier, so
 * a row that must not render is never sent rather than sent and hidden.
 *
 * The control is `List`, a real focusable button with an accessible name.
 * `ClipboardList` was rejected because the surrounding card headers already
 * carry a clipboard glyph, and `ListChecks` because its check marks would
 * imply the items had been verified or actioned. */
function DrugRisks({ entry }: { entry: DailyMedSevereEffectsForDrug }) {
  const [open, setOpen] = useState(false);
  const effects = entry.effects ?? [];
  const boxed = effects.filter((e) => e.tier === 'BOXED_WARNING');
  const contra = effects.filter((e) => e.tier === 'CONTRAINDICATION');
  const { name } = splitRxNorm(entry.drug);
  // The digest applies only while collapsed, and only when the backend
  // actually produced one. An older wrapper omits it and the full list shows.
  const digest = !open && entry.contraindications_summary ? entry.contraindications_summary : '';
  const expandable = Boolean(entry.contraindications_summary) && contra.length > 0;
  return (
    <div className="space-y-1.5">
      <div className="flex items-start gap-2">
        <span className="mt-[9px] h-1.5 w-1.5 rounded-full bg-brand/80 shrink-0" />
        <span className="text-[13px]">
          <DrugName display={entry.drug} />
        </span>
        {expandable ? (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-label={`Show all label-reported items for ${name}`}
            className="mt-[2px] inline-flex shrink-0 items-center rounded text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <List className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        ) : null}
      </div>
      <div className="ml-5 space-y-2">
        <TierBlock tier="BOXED_WARNING" rows={boxed} />
        <TierBlock tier="CONTRAINDICATION" rows={contra} digest={digest} />
        {effects.length === 0 ? (
          <p className="m-0 text-[12px] text-muted-foreground">
            {entry.absence || 'No label-reported risk found for this medication.'}
          </p>
        ) : null}
      </div>
    </div>
  );
}

export function DailyMedSafetyPanel({ data }: { data: DailyMedPanelData }) {
  const sections = data.sections ?? [];
  const severe: DailyMedSevereEffectsForDrug[] = data.severe_effects ?? [];
  return (
    <div className="space-y-4" data-testid="dailymed-panel">
      {sections.map((section) => (
        <div key={section.severity} className="space-y-1.5">
          <SectionHeader text={section.label} />
          {section.items.length === 0 ? (
            <EmptyLine />
          ) : (
            <ul className="space-y-1.5">
              {section.items.map((item, i) => (
                <li key={i} className="flex gap-2">
                  <span className="mt-[9px] h-1.5 w-1.5 rounded-full bg-brand/80 shrink-0" />
                  <span className="min-w-0 flex-1 text-[13px] leading-[1.65]">
                    <ItemSubjects item={item} />
                    {item.action}
                    <ItemExplainer item={item} />
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      ))}
      <div className="space-y-1.5">
        {/* #63: no trailing colon. Rule 16 fixes the subsection vocabulary as
            ALL CAPS, small, accent teal, letter-spaced, and carries punctuation
            on no other label, so a colon here read as a different kind of
            element on the same card.
            RENAMED at the same time, because #41 removed the Warning and
            Adverse-event tiers from this block: what remains is boxed warnings
            and contraindications, and a boxed warning is not a reported side
            effect. Keeping the old name would have left a true label on false
            content. */}
        <SectionHeader text="Label warnings by drug" />
        {severe.length === 0 ? (
          <EmptyLine />
        ) : (
          <div className="space-y-2.5">
            {severe.map((entry, i) => (
              <DrugRisks key={i} entry={entry} />
            ))}
          </div>
        )}
      </div>
      {data.corpus ? (
        <p className="m-0 text-[11.5px] italic text-muted-foreground">Source: {data.corpus}</p>
      ) : null}
    </div>
  );
}
